import { toMessages } from '@src/helpers/serverErrors';
import { EvaluationCase, EvaluationResult, EvaluationRowResult } from './types';

/* -------------------------------------------------------------------------- */
/*  Reading a run                                                             */
/* -------------------------------------------------------------------------- */

/**
 * The accuracy as a percentage, or null when there is no accuracy to show.
 *
 * The API reports `accuracy` as a number **only** while the run is `OK`; it is
 * null before a run, during one and after a failed one. Formatting it anyway is
 * what put `NaN%` on the page (`parseFloat(null) * 100`), so the single rule
 * here is that a null never reaches a number formatter.
 */
export const formatAccuracy = (result: EvaluationResult): string | null => {
    if (result.state !== 'OK') return null;
    const { accuracy } = result;
    if (typeof accuracy !== 'number' || !Number.isFinite(accuracy)) return null;
    return `${Math.round(accuracy * 100)}%`;
};

export type RunTone = 'idle' | 'running' | 'good' | 'bad';

export type RunStatus = {
    label: string;
    tone: RunTone;
    /** the second line, when there is more to say than the label */
    detail?: string;
};

/** rows that have come back one way or the other */
const answered = (results: EvaluationRowResult[]) =>
    results.filter((row) => row.state === 'OK' || row.state === 'FAILED').length;

/**
 * What to put where the score goes. Every state has a sentence of its own, so
 * "no score yet" is never mistaken for a score of nothing.
 */
export const describeRun = (result: EvaluationResult): RunStatus => {
    if (result.state === 'WAITING_FOR_RESPONSE') {
        const total = result.total || result.results.length;
        return {
            label: 'Running…',
            tone: 'running',
            detail: total ? `${answered(result.results)} of ${total} answered` : undefined,
        };
    }

    if (result.state === 'FAILED') {
        return { label: 'Failed', tone: 'bad', detail: 'Some cases did not get a verdict' };
    }

    if (result.state === 'OK') {
        const accuracy = formatAccuracy(result);
        return {
            // an OK run with no number behind it is a contradiction, but the
            // page still has to render something that is not NaN
            label: accuracy ?? 'No score reported',
            tone: accuracy && result.passed === result.total ? 'good' : 'bad',
            // short enough to sit beside the score rather than under it
            detail: `${result.passed} of ${result.total} matched`,
        };
    }

    return { label: 'Not run yet', tone: 'idle' };
};

/** a run id is 32 hex characters — enough of it to quote in a support ticket */
export const shortRunId = (runId: string | null) => (runId ? runId.slice(0, 8) : '');

/* -------------------------------------------------------------------------- */
/*  Reading one graded case                                                   */
/* -------------------------------------------------------------------------- */

/** the two verdicts, said the same way wherever one is shown */
export const verdictLabel = (verdict: boolean | null | undefined) => {
    if (verdict === true) return 'Approved';
    if (verdict === false) return 'Rejected';
    return '—';
};

/** the chip's colour: the verdict, or the absence of one */
export const verdictTone = (verdict: boolean | null | undefined) => {
    if (verdict === true) return 'yes';
    if (verdict === false) return 'no';
    return 'none';
};

/**
 * A declaration on one line, for a row that has the document itself a click
 * away.
 *
 * Built up rather than sliced out of `JSON.stringify`: dropping the quotes
 * around keys is what makes it read as a summary, and a regex over the
 * serialised form would do that to the inside of a string value too.
 */
const oneLine = (value: any): string => {
    if (Array.isArray(value)) return `[${value.map(oneLine).join(', ')}]`;
    if (value && typeof value === 'object') {
        return `{${Object.entries(value)
            .map(([key, item]) => `${key}: ${oneLine(item)}`)
            .join(', ')}}`;
    }
    return JSON.stringify(value) ?? String(value);
};

export const declarationPreview = (declaration: any, limit = 140): string => {
    if (declaration === null || declaration === undefined) return '';
    let text: string;
    try {
        text = oneLine(declaration);
    } catch (e) {
        return '';
    }
    return text.length > limit ? `${text.slice(0, limit - 1)}…` : text;
};

/* -------------------------------------------------------------------------- */
/*  Writing cases                                                             */
/* -------------------------------------------------------------------------- */

/** two cases are the same case when their declarations are, whatever the key order */
export const declarationKey = (declaration: any) => {
    try {
        return JSON.stringify(declaration, Object.keys(declaration ?? {}).sort());
    } catch (e) {
        return String(declaration);
    }
};

/** the API refuses duplicates outright, so say so before the round trip */
export const findDuplicate = (cases: EvaluationCase[], declaration: any, ignoreIndex?: number) =>
    cases.findIndex(
        (item, index) => index !== ignoreIndex && declarationKey(item.declaration) === declarationKey(declaration),
    );

/* -------------------------------------------------------------------------- */
/*  Server-side validation of the case list                                   */
/* -------------------------------------------------------------------------- */

export type CaseErrors = {
    /** about the list as a whole — "The evaluation cases must be unique." */
    general: string[];
    /** about one case, by its position in the list */
    byIndex: { [index: number]: string[] };
};

export const NO_CASE_ERRORS: CaseErrors = { general: [], byIndex: {} };

/** the field names the API uses, as the form names them */
const FIELD_LABELS: { [field: string]: string } = {
    declaration: 'Declaration',
    is_valid: 'Expected verdict',
};

/**
 * `extra_data.evaluation` out of a 400 body.
 *
 * DRF sends two different shapes down the same key: a list of strings when the
 * list as a whole is wrong, and one entry per case — `{}` for the ones that are
 * fine — when individual cases are. Both have to land next to the case they are
 * about, so both are read here rather than flattened into one toast.
 */
export const readCaseErrors = (body: any): CaseErrors => {
    const raw = body?.extra_data?.evaluation;
    if (raw === undefined || raw === null) return NO_CASE_ERRORS;

    const entries: any[] = Array.isArray(raw) ? raw : [raw];
    const general: string[] = [];
    const byIndex: { [index: number]: string[] } = {};

    entries.forEach((entry, index) => {
        if (typeof entry === 'string') {
            general.push(entry);
            return;
        }
        if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
            if (entry != null) general.push(...toMessages(entry));
            return;
        }
        const messages = Object.entries<any>(entry).flatMap(([field, value]) => {
            const label = FIELD_LABELS[field];
            return toMessages(value).map((message) => (label ? `${label}: ${message}` : message));
        });
        if (messages.length) byIndex[index] = messages;
    });

    return { general, byIndex };
};

export const hasCaseErrors = (errors: CaseErrors) =>
    errors.general.length > 0 || Object.keys(errors.byIndex).length > 0;

/**
 * The same body with the case errors taken out, for the form's own error
 * handling: they are shown against the cases they belong to, and the generic
 * handler would otherwise stringify the whole nested object into one message.
 */
export const withoutCaseErrors = (body: any) => {
    if (!body || typeof body !== 'object' || Array.isArray(body)) return body;
    if (!body.extra_data || typeof body.extra_data !== 'object') return body;
    const { evaluation, ...extraData } = body.extra_data;
    if (evaluation === undefined) return body;
    const rest = { ...body };
    if (Object.keys(extraData).length) rest.extra_data = extraData;
    else delete rest.extra_data;
    return rest;
};
